import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Box, Typography, TextField, Select, MenuItem, FormControl, InputLabel,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  TablePagination, IconButton, Button, Chip, Dialog, DialogActions,
  DialogContent, DialogContentText, DialogTitle, Snackbar, Alert,
  InputAdornment, Tooltip, CircularProgress, Checkbox, Autocomplete,
} from "@mui/material";
import {
  Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon,
  Search as SearchIcon, FilterList as FilterIcon,
  FileDownload as ExportIcon, FileUpload as ImportIcon,
} from "@mui/icons-material";
import AdminLayout from "../../components/AdminLayout";
import Loader from "../../components/Loading/Loader";
import {
  getExerciseList, getExerciseLookups, deleteExercise,
  exportExercises, importExercises, bulkClassifyExercises,
} from "../../components/funtionsBD/exerciseManagement";

const getLangField = (obj, prefix, currentLang) => {
  if (!obj) return "";
  const l = (currentLang || "en").substring(0, 2);
  const v = obj[`${prefix}_${l}`] || obj[`${prefix}_en`] || obj[prefix];
  return (v != null && String(v).trim() !== "") ? String(v).trim() : (obj[`${prefix}_en`] || obj[prefix] || "");
};

function exerciseCbs(ex) {
  const rows = ex?.tbl_exe_cbs || ex?.tbl_exe_cb || [];
  return Array.isArray(rows) ? rows : [];
}

function typeLabel(ex, lang) {
  return getLangField(ex?.tbl_type_ex, "type_name", lang);
}

function subTypeLabel(ex, lang) {
  return getLangField(ex?.tbl_sub_type_ex, "sub_type_name", lang);
}

function cbLabel(cb, lang) {
  return getLangField(cb?.tbl_blocks_compentency || cb, "cb_name", lang);
}

function useAppLanguage() {
  const { i18n } = useTranslation();
  const [lang, setLang] = useState(() => {
    const stored = localStorage.getItem("i18nextLng") || localStorage.getItem("lng") || i18n.language || "en";
    return String(stored).substring(0, 2);
  });
  useEffect(() => {
    const update = () => {
      const stored = localStorage.getItem("i18nextLng") || localStorage.getItem("lng") || i18n.language || "en";
      setLang(String(stored).substring(0, 2));
    };
    update();
    i18n.on("languageChanged", update);
    return () => i18n.off("languageChanged", update);
  }, [i18n]);
  return lang;
}

export function ExerciseManagement() {
  const { t } = useTranslation();
  const lang = useAppLanguage();
  const navigate = useNavigate();

  const [exercises, setExercises] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [subTypeFilter, setSubTypeFilter] = useState("");
  const [cbFilter, setCbFilter] = useState("");
  const [types, setTypes] = useState([]);
  const [subTypes, setSubTypes] = useState([]);
  const [competencyBlocks, setCompetencyBlocks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, exercise: null });
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [importDialog, setImportDialog] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importMode, setImportMode] = useState("merge");
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkField, setBulkField] = useState(null);
  const [bulkTypeId, setBulkTypeId] = useState("");
  const [bulkSubTypeId, setBulkSubTypeId] = useState("");
  const [bulkCbIds, setBulkCbIds] = useState([]);
  const [bulkSaving, setBulkSaving] = useState(false);

  const fetchLookups = useCallback(async () => {
    try {
      const res = await getExerciseLookups();
      setTypes(res.data.data.types || []);
      setSubTypes(res.data.data.subTypes || []);
      setCompetencyBlocks(res.data.data.competencyBlocks || []);
    } catch (err) {
      console.error("Failed to load lookups", err);
    }
  }, []);

  const fetchExercises = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: page + 1,
        limit: rowsPerPage,
      };
      if (search.trim()) params.search = search.trim();
      if (typeFilter) params.type_id = typeFilter;
      if (subTypeFilter) params.sub_type_id = subTypeFilter;
      if (cbFilter) params.cb_id = cbFilter;

      const res = await getExerciseList(params);
      setExercises(res.data.data.exercises || []);
      setTotal(res.data.data.total || 0);
    } catch (err) {
      console.error("Failed to load exercises", err);
      setSnackbar({ open: true, message: t("failed_to_load_data"), severity: "error" });
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, search, typeFilter, subTypeFilter, cbFilter, t]);

  useEffect(() => { fetchLookups(); }, [fetchLookups]);
  useEffect(() => { fetchExercises(); }, [fetchExercises]);

  const handleDelete = async () => {
    if (!deleteDialog.exercise) return;
    try {
      await deleteExercise(deleteDialog.exercise.ex_id);
      setSnackbar({ open: true, message: t("exerciseDeleted"), severity: "success" });
      setDeleteDialog({ open: false, exercise: null });
      fetchExercises();
    } catch (err) {
      setSnackbar({ open: true, message: err.message || t("error"), severity: "error" });
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await exportExercises();
      const blob = new Blob([response.data], { type: "application/json" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `exercises_backup_${new Date().toISOString().slice(0, 10)}.json`;
      link.click();
      window.URL.revokeObjectURL(url);
      setSnackbar({ open: true, message: t("exportSuccess"), severity: "success" });
    } catch (err) {
      setSnackbar({ open: true, message: err.message || t("error"), severity: "error" });
    } finally {
      setExporting(false);
    }
  };

  const handleImport = async () => {
    if (!importFile) return;
    setImporting(true);
    try {
      const response = await importExercises(importFile, importMode);
      const s = response.data.data;
      const ex = s.exercises || {};
      const tpl = s.templates || {};
      const parts = [`${t("exercises")}: ${ex.created || 0} ${t("created")}, ${ex.updated || 0} ${t("updated")}, ${ex.skipped || 0} ${t("skipped")}`];
      if (tpl.created || tpl.updated || tpl.skipped) {
        parts.push(`${t("templates")}: ${tpl.created || 0} ${t("created")}, ${tpl.updated || 0} ${t("updated")}, ${tpl.skipped || 0} ${t("skipped")}`);
      }
      setSnackbar({
        open: true,
        message: `${t("importSuccess")} – ${parts.join(" | ")}`,
        severity: s.errors?.length ? "warning" : "success",
      });
      setImportDialog(false);
      setImportFile(null);
      setImportMode("merge");
      fetchExercises();
    } catch (err) {
      setSnackbar({ open: true, message: err.message || t("error"), severity: "error" });
    } finally {
      setImporting(false);
    }
  };

  const filteredSubTypes = subTypes.filter(
    (st) => !typeFilter || st.type_id === typeFilter || !st.type_id
  );
  const bulkSubTypes = subTypes.filter(
    (st) => !bulkTypeId || st.type_id === bulkTypeId || !st.type_id
  );
  const pageIds = exercises.map((ex) => ex.ex_id).filter(Boolean);
  const allPageSelected = pageIds.length > 0 && pageIds.every((id) => selectedIds.includes(id));
  const somePageSelected = pageIds.some((id) => selectedIds.includes(id));

  const toggleRow = (exId) => {
    setSelectedIds((prev) =>
      prev.includes(exId) ? prev.filter((id) => id !== exId) : [...prev, exId]
    );
  };

  const togglePage = () => {
    setSelectedIds((prev) => {
      if (allPageSelected) return prev.filter((id) => !pageIds.includes(id));
      const next = new Set(prev);
      pageIds.forEach((id) => next.add(id));
      return [...next];
    });
  };

  const openBulk = (field) => {
    setBulkField(field);
    setBulkTypeId(typeFilter || "");
    setBulkSubTypeId("");
    setBulkCbIds([]);
  };

  const handleBulkApply = async () => {
    if (!selectedIds.length || !bulkField) return;
    const payload = { ex_ids: selectedIds };
    if (bulkField === "type") {
      if (!bulkTypeId) return;
      payload.type_id = bulkTypeId;
    } else if (bulkField === "subtype") {
      payload.sub_type_id = bulkSubTypeId || null;
    } else {
      payload.competency_blocks = bulkCbIds;
    }
    setBulkSaving(true);
    try {
      const res = await bulkClassifyExercises(payload);
      const n = res.data?.data?.updated || selectedIds.length;
      setSnackbar({
        open: true,
        message: t("studioBulkSuccess", { count: n }) || `Updated ${n} exercises`,
        severity: "success",
      });
      setBulkField(null);
      setSelectedIds([]);
      fetchExercises();
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.message || t("error"),
        severity: "error",
      });
    } finally {
      setBulkSaving(false);
    }
  };

  return (
    <AdminLayout>
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
          <Typography variant="h5" fontWeight="bold">
            {t("exerciseStudio")}
          </Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={exporting ? <CircularProgress size={18} /> : <ExportIcon />}
              onClick={handleExport}
              disabled={exporting}
            >
              {t("export")}
            </Button>
            <Button
              variant="outlined"
              startIcon={<ImportIcon />}
              onClick={() => setImportDialog(true)}
            >
              {t("import")}
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate("/admin/exercise/new")}
              sx={{ bgcolor: "var(--brand-primary)", "&:hover": { bgcolor: "var(--brand-primary-dark)" } }}
            >
              {t("addExerciseCatalog")}
            </Button>
          </Box>
        </Box>

        {/* Filters */}
        <Paper sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
            <TextField
              size="small"
              placeholder={t("searchExercises")}
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(0); }}
              sx={{ minWidth: 250 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start"><SearchIcon /></InputAdornment>
                ),
              }}
            />
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>{t("exerciseType")}</InputLabel>
              <Select
                value={typeFilter}
                label={t("exerciseType")}
                onChange={(e) => { setTypeFilter(e.target.value); setSubTypeFilter(""); setPage(0); }}
              >
                <MenuItem value="">{t("all")}</MenuItem>
                {types.map((tp) => (
                  <MenuItem key={tp.type_id} value={tp.type_id}>
                    {getLangField(tp, "type_name", lang)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>{t("exerciseSubType")}</InputLabel>
              <Select
                value={subTypeFilter}
                label={t("exerciseSubType")}
                onChange={(e) => { setSubTypeFilter(e.target.value); setPage(0); }}
              >
                <MenuItem value="">{t("all")}</MenuItem>
                {filteredSubTypes.map((st) => (
                  <MenuItem key={st.sub_type_id} value={st.sub_type_id}>
                    {getLangField(st, "sub_type_name", lang)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel>{t("competencyblock") || t("competencyBlocks")}</InputLabel>
              <Select
                value={cbFilter}
                label={t("competencyblock") || t("competencyBlocks")}
                onChange={(e) => { setCbFilter(e.target.value); setPage(0); }}
              >
                <MenuItem value="">{t("all")}</MenuItem>
                {competencyBlocks.map((cb) => (
                  <MenuItem key={cb.cb_id} value={cb.cb_id}>
                    {getLangField(cb, "cb_name", lang)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Chip
              label={`${total} ${t("exercises")}`}
              color="primary"
              variant="outlined"
              icon={<FilterIcon />}
            />
          </Box>
        </Paper>

        {selectedIds.length > 0 ? (
          <Paper
            sx={{
              p: 1.5,
              mb: 2,
              display: "flex",
              flexWrap: "wrap",
              gap: 1,
              alignItems: "center",
              border: "1px solid color-mix(in srgb, var(--brand-primary, #0877BC) 28%, transparent)",
            }}
          >
            <Typography variant="body2" fontWeight={700} sx={{ mr: 1 }}>
              {t("studioSelectedCount", { count: selectedIds.length }) ||
                `${selectedIds.length} selected`}
            </Typography>
            <Button size="small" variant="contained" onClick={() => openBulk("type")}>
              {t("studioChangeType") || "Change type"}
            </Button>
            <Button size="small" variant="contained" onClick={() => openBulk("subtype")}>
              {t("studioChangeSubType") || "Change subtype"}
            </Button>
            <Button size="small" variant="contained" onClick={() => openBulk("competency")}>
              {t("studioChangeCompetencyBlock") || "Change competency block"}
            </Button>
            <Button size="small" onClick={() => setSelectedIds([])}>
              {t("studioClearSelection") || "Clear selection"}
            </Button>
          </Paper>
        ) : null}

        {/* Table */}
        <TableContainer component={Paper}>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: "#f5f5f5" }}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      checked={allPageSelected}
                      indeterminate={somePageSelected && !allPageSelected}
                      onChange={togglePage}
                      inputProps={{
                        "aria-label": t("studioSelectExercises") || "Select exercises",
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>{t("name")}</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>{t("exerciseType")}</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>{t("exerciseSubType")}</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>{t("cardioLevel")}</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>{t("competencyblock") || t("competencyBlocks")}</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }} align="right">{t("actions")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {exercises.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">{t("noExercisesFound")}</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  exercises.map((ex) => {
                    const checked = selectedIds.includes(ex.ex_id);
                    const cbs = exerciseCbs(ex);
                    const typeName = typeLabel(ex, lang);
                    const subName = subTypeLabel(ex, lang);
                    return (
                    <TableRow
                      key={ex.ex_id}
                      hover
                      selected={checked}
                      sx={{ cursor: "pointer" }}
                      onClick={() => {
                        if (selectedIds.length > 0) toggleRow(ex.ex_id);
                        else navigate(`/admin/exercise/edit/${ex.ex_id}`);
                      }}
                    >
                      <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          size="small"
                          checked={checked}
                          onChange={() => toggleRow(ex.ex_id)}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {getLangField(ex, "ex_name", lang)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {typeName ? (
                          <Chip
                            label={typeName}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        ) : (
                          <Typography variant="caption" color="text.secondary">—</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {subName ? (
                          <Chip
                            label={subName}
                            size="small"
                            variant="outlined"
                          />
                        ) : (
                          <Typography variant="caption" color="text.secondary">—</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {ex.ex_cardio_lev ? (
                          <Box sx={{ display: "flex", gap: 0.25 }}>
                            {[1, 2, 3, 4, 5].map((i) => (
                              <Box
                                key={i}
                                sx={{
                                  width: 8, height: 8, borderRadius: "50%",
                                  bgcolor: i <= ex.ex_cardio_lev ? "#f44336" : "#e0e0e0",
                                }}
                              />
                            ))}
                          </Box>
                        ) : "—"}
                      </TableCell>
                      <TableCell>
                        {cbs.length ? (
                          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                            {cbs.map((cb) => {
                              const label = cbLabel(cb, lang);
                              if (!label) return null;
                              return (
                                <Chip
                                  key={cb.cb_id || label}
                                  label={label}
                                  size="small"
                                  variant="outlined"
                                  color="secondary"
                                />
                              );
                            })}
                          </Box>
                        ) : (
                          <Typography variant="caption" color="text.secondary">—</Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title={t("editExercise")}>
                          <IconButton
                            size="small"
                            onClick={(e) => { e.stopPropagation(); navigate(`/admin/exercise/edit/${ex.ex_id}`); }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t("delete")}>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={(e) => { e.stopPropagation(); setDeleteDialog({ open: true, exercise: ex }); }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          )}
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value)); setPage(0); }}
            rowsPerPageOptions={[10, 20, 50]}
          />
        </TableContainer>
      </Box>

      {/* Delete confirmation */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, exercise: null })}>
        <DialogTitle>{t("confirmDelete")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("confirmDeleteExercise")} <br />
            <strong>{getLangField(deleteDialog.exercise, "ex_name", lang)}</strong>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, exercise: null })}>{t("cancel")}</Button>
          <Button onClick={handleDelete} color="error" variant="contained">{t("delete")}</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(bulkField)}
        onClose={() => { if (!bulkSaving) setBulkField(null); }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {bulkField === "type"
            ? t("studioChangeType") || "Change type"
            : bulkField === "subtype"
              ? t("studioChangeSubType") || "Change subtype"
              : t("studioChangeCompetencyBlock") || "Change competency block"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {t("studioBulkConfirm", { count: selectedIds.length }) ||
              `Update ${selectedIds.length} exercises`}
          </DialogContentText>
          {bulkField === "type" ? (
            <FormControl fullWidth>
              <InputLabel>{t("exerciseType")}</InputLabel>
              <Select
                value={bulkTypeId}
                label={t("exerciseType")}
                onChange={(e) => setBulkTypeId(e.target.value)}
                disabled={bulkSaving}
              >
                {types.map((tp) => (
                  <MenuItem key={tp.type_id} value={tp.type_id}>
                    {getLangField(tp, "type_name", lang)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : null}
          {bulkField === "subtype" ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel>{t("exerciseType")}</InputLabel>
                <Select
                  value={bulkTypeId}
                  label={t("exerciseType")}
                  onChange={(e) => {
                    setBulkTypeId(e.target.value);
                    setBulkSubTypeId("");
                  }}
                  disabled={bulkSaving}
                >
                  <MenuItem value="">{t("all")}</MenuItem>
                  {types.map((tp) => (
                    <MenuItem key={tp.type_id} value={tp.type_id}>
                      {getLangField(tp, "type_name", lang)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>{t("exerciseSubType")}</InputLabel>
                <Select
                  value={bulkSubTypeId}
                  label={t("exerciseSubType")}
                  onChange={(e) => setBulkSubTypeId(e.target.value)}
                  disabled={bulkSaving}
                >
                  <MenuItem value="">{t("studioClearValue") || "Clear subtype"}</MenuItem>
                  {bulkSubTypes.map((st) => (
                    <MenuItem key={st.sub_type_id} value={st.sub_type_id}>
                      {getLangField(st, "sub_type_name", lang)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          ) : null}
          {bulkField === "competency" ? (
            <Autocomplete
              multiple
              options={competencyBlocks}
              value={competencyBlocks.filter((cb) => bulkCbIds.includes(cb.cb_id))}
              onChange={(_, next) => setBulkCbIds(next.map((cb) => cb.cb_id))}
              isOptionEqualToValue={(a, b) => a?.cb_id === b?.cb_id}
              getOptionLabel={(cb) => getLangField(cb, "cb_name", lang) || cb.cb_id}
              disabled={bulkSaving}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t("competencyblock") || t("competencyBlocks")}
                  placeholder={t("search")}
                  helperText={t("studioBulkCbHint") || "Leave empty to remove competency blocks from the selected exercises."}
                />
              )}
            />
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkField(null)} disabled={bulkSaving}>{t("cancel")}</Button>
          <Button
            onClick={handleBulkApply}
            variant="contained"
            disabled={bulkSaving || (bulkField === "type" && !bulkTypeId)}
            startIcon={bulkSaving ? <CircularProgress size={18} /> : null}
            sx={{ bgcolor: "var(--brand-primary)", "&:hover": { bgcolor: "var(--brand-primary-dark)" } }}
          >
            {t("studioBulkApply") || "Update selected"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Import dialog */}
      <Dialog
        open={importDialog}
        onClose={() => { if (!importing) { setImportDialog(false); setImportFile(null); } }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t("importExercises")}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {t("importExercisesDescription")}
          </DialogContentText>

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>{t("importMode")}</InputLabel>
            <Select
              value={importMode}
              label={t("importMode")}
              onChange={(e) => setImportMode(e.target.value)}
              disabled={importing}
            >
              <MenuItem value="merge">{t("importModeMerge")}</MenuItem>
              <MenuItem value="overwrite">{t("importModeOverwrite")}</MenuItem>
            </Select>
          </FormControl>

          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
            {importMode === "merge" ? t("importModeMergeHelp") : t("importModeOverwriteHelp")}
          </Typography>

          <Button variant="outlined" component="label" fullWidth sx={{ mt: 1 }} disabled={importing}>
            {importFile ? importFile.name : t("selectJsonFile")}
            <input
              type="file"
              accept=".json"
              hidden
              onChange={(e) => setImportFile(e.target.files?.[0] || null)}
            />
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setImportDialog(false); setImportFile(null); }} disabled={importing}>
            {t("cancel")}
          </Button>
          <Button
            onClick={handleImport}
            variant="contained"
            disabled={!importFile || importing}
            startIcon={importing ? <CircularProgress size={18} /> : <ImportIcon />}
            sx={{ bgcolor: "var(--brand-primary)", "&:hover": { bgcolor: "var(--brand-primary-dark)" } }}
          >
            {importing ? t("importing") : t("import")}
          </Button>
        </DialogActions>
      </Dialog>
    </AdminLayout>
  );
}
